import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Star, ArrowRight, ArrowUpRight, Users, Award, TrendingUp, Check, X } from "lucide-react";
import { IMAGES, PROGRAMS, TESTIMONIALS, STORIES } from "../data/site";
import { Reveal, Label, BookButton, Marquee } from "../components/ui-kit";

const HERO_LINES = ["Become the", "strongest version", "of yourself."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen min-h-[680px] overflow-hidden grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster.jpg"
          className="w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="/hero.webm" type="video/webm" />
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Label className="mb-6">Hyrox & Functional Fitness · Pictou County</Label>
        </motion.div>

        <h1 className="font-display uppercase text-white leading-[0.9] text-5xl sm:text-7xl md:text-8xl max-w-4xl">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? <span className="text-gold">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 text-lg md:text-xl text-lightgrey max-w-xl"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
        >
          Coached group fitness built for everyday people in Pictou County. No egos. No confusion. Just real coaching and a community that has your back.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
        >
          <BookButton testid="hero-book">Start Your Free Intro</BookButton>
          <Link to="/schedule" data-testid="hero-schedule" className="inline-flex items-center gap-2 rounded-full border border-gold text-gold font-bold px-8 py-4 hover:bg-gold hover:text-black transition">
            View Schedule <ArrowRight size={18} />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 text-xs uppercase tracking-[0.3em]"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
      >
        Scroll
      </motion.div>
    </section>
  );
}

function SocialProof() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 4500);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[idx];
  return (
    <section className="bg-charcoal py-16" data-testid="social-proof">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-[auto_1fr] gap-10 items-center">
        <div className="text-center md:text-left md:border-r md:border-white/10 md:pr-10">
          <div className="flex justify-center md:justify-start gap-1 text-gold mb-3">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="#d1af37" />)}
          </div>
          <div className="flex justify-center md:justify-start gap-8">
            <div>
              <p className="font-display text-5xl text-white leading-none">35+</p>
              <p className="text-lightgrey text-xs uppercase tracking-wide mt-1">Members & Growing</p>
            </div>
            <div>
              <p className="font-display text-5xl text-white leading-none">2</p>
              <p className="text-lightgrey text-xs uppercase tracking-wide mt-1">Dryland Teams Coached</p>
            </div>
          </div>
          <p className="text-white/50 text-xs mt-4 max-w-xs md:mx-0 mx-auto">Proudly serving Pictou County since May 2025 — including dryland training for local Soccer & Swimming teams.</p>
        </div>
        <div className="min-h-[90px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-xl md:text-2xl text-white font-display leading-snug">"{t.quote}"</p>
              <footer className="mt-3 text-sm text-gold uppercase tracking-wide">{t.name} — <span className="text-white/50">{t.role}</span></footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

const WHY = [
  { icon: Award, title: "Expert Coaching", desc: "Every workout is coached from start to finish. You'll always know what to do and how to do it safely.", span: "md:col-span-2", img: IMAGES.coachTalk },
  { icon: Users, title: "Community", desc: "You'll never feel like you're working out alone. Our members become your biggest supporters.", span: "", img: null },
  { icon: TrendingUp, title: "Real Results", desc: "Build strength, improve endurance, and gain the confidence that carries into every part of your life.", span: "", img: null },
];

function WhyHFC() {
  return (
    <section className="bg-black py-24 md:py-32" data-testid="why-hfc">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Label>Why HFC</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 max-w-2xl leading-none">A gym that actually has your back.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08} className={w.span}>
              <div className="group relative h-full rounded-3xl border border-white/10 bg-charcoal p-8 overflow-hidden hover:border-gold/40 transition-all hover:-translate-y-1 duration-300">
                {w.img && (
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition">
                    <img src={w.img} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/80 to-transparent" />
                  </div>
                )}
                <div className="relative z-10">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-gold/15 border border-gold/30 items-center justify-center text-gold mb-6">
                    <w.icon size={22} />
                  </span>
                  <h3 className="font-display text-2xl md:text-3xl text-white uppercase">{w.title}</h3>
                  <p className="mt-3 text-lightgrey max-w-md">{w.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProgramsGrid() {
  return (
    <section className="bg-black pb-24 md:pb-32" data-testid="programs">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-14">
          <div>
            <Reveal><Label>Programs</Label></Reveal>
            <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">Find your<br />starting line.</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/programs" className="inline-flex items-center gap-2 text-gold hover:text-white transition font-bold uppercase text-sm tracking-wide" data-testid="programs-all">
              All Programs <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.07}>
              <Link to={`/programs/${p.slug}`} data-testid={`program-${p.slug}`} className="group block rounded-3xl overflow-hidden border border-white/10 bg-charcoal hover:border-gold/40 transition-all hover:-translate-y-1 duration-300 h-full">
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl text-white uppercase">{p.title}</h3>
                  <p className="text-sm text-gold mt-1">{p.tagline}</p>
                  <p className="mt-3 text-sm text-lightgrey/80">{p.blurb}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-white text-sm font-bold group-hover:text-gold transition">Learn More <ArrowRight size={16} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const TRAD = ["Figure it out yourself", "Crowded and intimidating", "No accountability", "Generic workouts"];
const HFC = ["Professional coaching", "Structured programming", "Friendly community", "Progress tracking"];

function Comparison() {
  return (
    <section className="bg-charcoal py-24 md:py-32 grain relative" data-testid="comparison">
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal className="text-center">
          <Label className="justify-center">What Makes Us Different</Label>
          <h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">This isn't your<br />average gym.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-16">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-black/40 p-8 h-full">
              <h3 className="font-display text-2xl text-white/50 uppercase mb-6">Traditional Gym</h3>
              <ul className="space-y-4">
                {TRAD.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-white/60">
                    <span className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center shrink-0"><X size={16} className="text-red-400/70" /></span>{t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-gold/40 bg-gradient-to-b from-gold/10 to-transparent p-8 h-full relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold/20 blur-3xl rounded-full" />
              <h3 className="font-display text-2xl text-gold uppercase mb-6 relative">Hybrid Fitness Collective</h3>
              <ul className="space-y-4 relative">
                {HFC.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-white">
                    <span className="w-7 h-7 rounded-full bg-gold flex items-center justify-center shrink-0"><Check size={16} className="text-black" /></span>{t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function StoriesCarousel() {
  const [i, setI] = useState(0);
  const next = () => setI((v) => (v + 1) % STORIES.length);
  const prev = () => setI((v) => (v - 1 + STORIES.length) % STORIES.length);
  const s = STORIES[i];
  return (
    <section className="bg-black py-24 md:py-32" data-testid="stories-carousel">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Label>Member Success Stories</Label></Reveal>
        <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">Real people.<br />Real change.</h2></Reveal>
        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-center">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
              <img src={s.image} alt={s.name} className="w-full h-full object-cover" style={{ objectPosition: s.pos || "center" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            </motion.div>
          </AnimatePresence>
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <p className="font-display text-3xl md:text-4xl text-gold uppercase">{s.name}</p>
                <p className="mt-2 text-white text-lg">{s.stat}</p>
                <p className="mt-6 font-display text-2xl md:text-3xl text-white leading-snug">"{s.quote}"</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-10 flex items-center gap-4">
              <button onClick={prev} data-testid="story-prev" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black hover:border-gold transition"><ArrowRight size={18} className="rotate-180" /></button>
              <button onClick={next} data-testid="story-next" className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black hover:border-gold transition"><ArrowRight size={18} /></button>
              <span className="text-white/40 text-sm ml-2">{i + 1} / {STORIES.length}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTABanner() {
  return (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="cta-banner">
      <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/9840453e3707ca3f_renfrewatlanticseries4980.webp" alt="HFC members during a kettlebell workout" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/75" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-display uppercase text-white text-5xl md:text-7xl leading-none">Ready to get<br /><span className="text-gold">started?</span></h2>
          <p className="mt-6 text-lg text-lightgrey max-w-xl mx-auto">Book your free intro today. It's the first step toward becoming the strongest version of yourself.</p>
          <div className="mt-10 flex justify-center">
            <BookButton testid="cta-banner-book" className="text-lg px-10 py-5">Book Your Free Intro</BookButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <SocialProof />
      <Marquee items={["Strength", "Community", "Hyrox", "Recovery", "Confidence", "Coaching"]} />
      <WhyHFC />
      <ProgramsGrid />
      <Comparison />
      <StoriesCarousel />
      <CTABanner />
    </>
  );
}
